import { Mail, Globe } from "lucide-react";
import SEO from "@/components/SEO";

export default function Press() {
  return (
    <div className="min-h-screen" data-testid="press-page">
      <SEO
        title="Press & Contact"
        description="Contact Filmcable for press inquiries, media partnerships, and festival accreditation. Email: press@filmcable.net"
        url="https://filmcable.net/press"
      />
      
      {/* Hero Section */}
      <section className="py-20 md:py-32 border-b border-zinc-200">
        <div className="container">
          <div className="max-w-4xl">
            <span className="font-accent text-sm font-bold uppercase tracking-widest text-[#FF66C4] mb-4 block">
              Press & Contact
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-none mb-8">
              Get in Touch
            </h1>
            <div className="accent-line mb-8" />
            <p className="text-xl md:text-2xl text-zinc-600 leading-relaxed">
              For media inquiries, press credentials, and partnership opportunities, 
              reach out to our editorial team.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-20 md:py-28">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Press Contact */}
            <div className="contact-card" data-testid="press-contact-card">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#FF66C4] flex items-center justify-center">
                  <Mail className="text-white" size={24} />
                </div>
                <h2 className="font-serif text-2xl font-bold">Press Inquiries</h2>
              </div>
              <p className="text-zinc-600 mb-6 leading-relaxed">
                For press releases, media coverage requests, PR agency communications, 
                and film festival accreditation inquiries.
              </p>
              <a
                href="mailto:press@filmcable.net"
                className="inline-flex items-center gap-2 text-xl font-bold text-[#FF66C4] hover:underline"
                data-testid="press-email-link"
              >
                press@filmcable.net
              </a>
            </div>

            {/* General Contact */}
            <div className="contact-card" data-testid="general-contact-card">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-zinc-900 flex items-center justify-center">
                  <Globe className="text-white" size={24} />
                </div>
                <h2 className="font-serif text-2xl font-bold">General Inquiries</h2>
              </div>
              <p className="text-zinc-600 mb-6 leading-relaxed">
                For general questions, editorial feedback, and other communications 
                with our team.
              </p>
              <a
                href="mailto:press@filmcable.net"
                className="inline-flex items-center gap-2 text-xl font-bold hover:text-[#FF66C4] transition-colors"
                data-testid="general-email-link"
              >
                press@filmcable.net
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* For PR & Festivals */}
      <section className="py-20 md:py-28 bg-zinc-50">
        <div className="container">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
              For PR Agencies & Film Festivals
            </h2>
            <div className="accent-line mb-8" />
            
            <div className="space-y-8">
              <div>
                <h3 className="font-serif text-xl font-bold mb-3">Press Releases</h3>
                <p className="text-zinc-600 leading-relaxed">
                  Submit press releases, screening invitations, and embargo materials to 
                  <a href="mailto:press@filmcable.net" className="text-[#FF66C4] hover:underline ml-1">
                    press@filmcable.net
                  </a>. Please include relevant assets, press kits, and contact information.
                </p>
              </div>
              
              <div>
                <h3 className="font-serif text-xl font-bold mb-3">Festival Accreditation</h3>
                <p className="text-zinc-600 leading-relaxed">
                  For press accreditation requests at film festivals, please contact us at least 
                  four weeks before the event. Include the festival name, dates, and specific 
                  coverage plans.
                </p>
              </div>
              
              <div>
                <h3 className="font-serif text-xl font-bold mb-3">Interview Requests</h3>
                <p className="text-zinc-600 leading-relaxed">
                  We welcome interview opportunities with filmmakers, actors, and industry 
                  professionals. Please provide relevant background materials and availability.
                </p>
              </div>
              
              <div>
                <h3 className="font-serif text-xl font-bold mb-3">Media Partnerships</h3>
                <p className="text-zinc-600 leading-relaxed">
                  For media partnership inquiries, sponsorship opportunities, and collaborative 
                  projects, please reach out with a detailed proposal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Response Time */}
      <section className="py-20 md:py-28 border-t border-zinc-200">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif text-2xl font-bold mb-4">Response Time</h2>
            <p className="text-zinc-600 leading-relaxed">
              We aim to respond to all press inquiries within 48 hours. For urgent matters 
              related to embargo deadlines or time-sensitive coverage, please indicate 
              "URGENT" in your subject line.
            </p>
            
            <div className="mt-12 p-8 border border-zinc-200 text-left">
              <h3 className="font-accent text-sm font-bold uppercase tracking-widest text-[#FF66C4] mb-4">
                Primary Press Contact
              </h3>
              <p className="font-serif text-2xl font-bold mb-2">Filmcable Editorial</p>
              <a
                href="mailto:press@filmcable.net"
                className="text-lg text-[#FF66C4] hover:underline"
                data-testid="footer-press-email"
              >
                press@filmcable.net
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
